import { ProductListApi } from '@common/api/product-list';
import { classNames } from '@common/application/utils/classnames';
import { getFirstImage } from '@common/application/utils/common';
import { Button } from '@common/components/common/button';
import { DImage } from '@common/components/common/image';
import type { ModalProps } from '@common/components/common/modal';
import { Modal } from '@common/components/common/modal';
import { Pagination } from '@common/components/common/pagination';
import { Price } from '@common/components/common/price';
import type { IProductListItem } from '@common/types/product-list';
import { useAsyncEffect } from 'ahooks';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';

import { ColorFillIcon } from '@/resources/sprites-images/color-fill-icon';

export interface ProductSelectorProps extends Omit<ModalProps, 'onConfirm'> {
  offlineCategoryId?: string;
  onConfirm(csku: string): void;
}

export const ProductSelector: React.FC<ProductSelectorProps> = (props) => {
  const { open, offlineCategoryId, onConfirm, ...modalProps } = props;

  const { t } = useTranslation();
  const [products, setProducts] = useState<IProductListItem[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 10;

  useEffect(() => setPage(1), [open]);

  useAsyncEffect(async () => {
    if (open && offlineCategoryId) {
      const { result, total } = await ProductListApi.getProductList({
        page,
        perPage,
        offlineCategoryIds: offlineCategoryId
      });
      setProducts(result);
      setTotal(total);
    }
  }, [open, page, offlineCategoryId]);

  return (
    <Modal
      {...modalProps}
      className={classNames('h-127.5', 'pc:h-110')}
      headerClassName={classNames('justify-center')}
      bodyClassName="flex flex-col items-center"
      title={t('product-diff:selectTitle')}
      open={open}
      destroyFooter
      destroyCloseIcon={false}
    >
      <div
        className={classNames(
          'flex-1 grid grid-cols-12 gap-x-4 gap-y-3 p-4 w-full overflow-y-scroll',
          'pc:p-6'
        )}
      >
        {products.map((product) => (
          <div
            key={product.csku}
            className={classNames(
              'col-span-12 flex p-2 h-22.5 border border-black-6 rounded',
              'pc:col-span-6 pc:p-3 pc:h-24.5'
            )}
          >
            <DImage
              className={classNames('flex-shrink-0 mr-2 w-18.5 h-18.5', 'pc:mr-3')}
              src={getFirstImage(product.customPictures)!}
              fill
            />
            <div className="flex-1 flex flex-col justify-between">
              <p
                className={classNames(
                  'h-8 text-xs text-black-1 line-clamp-2',
                  'pc:h-10 pc:text-sm'
                )}
              >
                {product.cskuName || product.name}
              </p>
              <div className="flex justify-between">
                <Price
                  className={classNames('text-xs font-semibold text-black-1', 'pc:text-sm')}
                  price={product.salePrice}
                />
                <Button
                  className="p-0 text-xs border-none"
                  color="black-1"
                  dark={false}
                  icon={
                    <ColorFillIcon
                      className={classNames('w-4 h-4', 'pc:w-5 pc:h-5')}
                      icon="compare"
                    />
                  }
                  onClick={() => onConfirm(product.csku)}
                >
                  {t('product-diff:diff')}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {perPage < total && (
        <div className={classNames('my-4', 'pc:my-6')}>
          <Pagination current={page} total={total} pageSize={perPage} onChange={setPage} />
        </div>
      )}
    </Modal>
  );
};
